import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { Customer, defaultPaymentsSettings, PaymentFileColumn, PaymentsSettings, PaymentRoute } from '../../../../core/models';
import {
  PaymentConfirmationType,
  PaymentPdfTextDialogComponent
} from './payment-pdf-text-dialog/payment-pdf-text-dialog.component';

@Component({
    selector: 'app-payments-settings',
    templateUrl: './payments-settings.component.html',
    styleUrls: ['./payments-settings.component.scss'],
    standalone: false
})
export class PaymentsSettingsComponent implements OnInit, OnChanges, OnDestroy {

  @Input() customer: Customer | undefined;
  @Input() settings: PaymentsSettings | undefined;
  @Output() saveSettings: EventEmitter<Record<string, unknown>> = new EventEmitter();

  private destroy$ = new Subject<void>();

  value: PaymentsSettings = {...defaultPaymentsSettings };
  form: FormGroup = new FormGroup( {
    notify_accounting: new FormControl<string>('', [Validators.maxLength(100), Validators.email]),
    notify_on_initial_addendum: new FormControl<boolean>(false),
    notify_on_additional_addendum: new FormControl<boolean>(false),
    additional_addendum_show_totals: new FormControl<boolean>(false),
    additional_addendum_show_past: new FormControl<boolean>(false),
    notify_on_outgoing: new FormControl<boolean>(false),
    outgoing_show_totals: new FormControl<boolean>(false),
    outgoing_show_past: new FormControl<boolean>(false),
    notify_on_incoming: new FormControl<boolean>(false),
    incoming_show_totals: new FormControl<boolean>(false),
    incoming_show_past: new FormControl<boolean>(false),
    notify_on_dropped: new FormControl<boolean>(false),
    dropped_show_totals: new FormControl<boolean>(false),
    dropped_show_past: new FormControl<boolean>(false),
    notify_on_transfer: new FormControl<boolean>(false),
    transfer_show_totals: new FormControl<boolean>(false),
    transfer_show_past: new FormControl<boolean>(false),
    notify_on_exchange_rate_diff: new FormControl<boolean>(false),
    notify_applicant: new FormControl<boolean>(false),
    'file-format': new FormControl<string>('PaymentsXlsx', [Validators.required]),
    'file-columns': new FormArray<FormGroup>([]),
    currency_based_routing: new FormArray<FormGroup>([]),
    use_p_no: new FormControl<boolean>(false),
    frequency_mode: new FormControl<string>('default'),
    frequency: new FormControl<number[]>([]),
    force_future: new FormControl<boolean>(false),
    skip_weeks: new FormControl<number[]>([]),
    export_cutoff: new FormControl<number>(3, [Validators.required, Validators.min(1), Validators.max(100)])
  });

  constructor(private dialog: MatDialog, private route: ActivatedRoute) {
  }

  ngOnInit(): void {
    this.form.disable();

    if (!this.customer) {
      this.route.data.pipe(takeUntil(this.destroy$)).subscribe(data => {
        if (data['customer']) {
          this.customer = data['customer'];
        }
      });
    }

    this.fileFormat.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateFileColumnsStatus();
      });

    this.currencyRoutes.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.validateCurrencies();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['settings']) {
      this.settings = changes['settings'].currentValue;
      if (this.settings) {
        this.value = { ...defaultPaymentsSettings, ...this.settings };
      }
      this.setFileColumns(this.value['file-columns']);
      this.setCurrencyRoutes(this.value.currency_based_routing);
      this.form.patchValue(this.toFormValue(this.value));
      this.updateFileColumnsStatus();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  doEdit(): void {
    this.form.enable();
    this.updateFileColumnsStatus();
  }

  doCancel(): void {
    this.value = { ...defaultPaymentsSettings, ...this.settings };
    this.setFileColumns(this.value['file-columns']);
    this.setCurrencyRoutes(this.value.currency_based_routing);
    this.form.reset(this.toFormValue(this.value));
    this.form.disable();
  }

  openPdfTextDialog(type: PaymentConfirmationType): void {
    this.dialog.open(PaymentPdfTextDialogComponent, {
      data: {
        customer: this.customer,
        type
      },
      width: '60vw'
    });
  }

  doSave(): void {
    // An empty format is no format, rather than a format of ''.
    this.fileColumns.controls.forEach((column) => {
      const format = column.get('format');
      if (!format?.value) {
        format?.setValue(null, { emitEvent: false });
      }
    });

    const routing: Record<string, PaymentRoute> = {};
    this.currencyRoutes.controls.forEach((routeGroup) => {
      const route = routeGroup.value as PaymentRoute;
      const currency = (route.currency || '').trim().toUpperCase();
      if (currency) {
        routing[currency] = {
          currency,
          account_no: (route.account_no || '').trim(),
          swift: (route.swift || '').trim().toUpperCase(),
          accounting: (route.accounting || '').trim()
        };
      }
    });

    // The routes are edited as a list, but stored keyed by currency.
    this.saveSettings.emit({ ...this.form.value, currency_based_routing: routing });
    this.form.disable();
  }

  doAddFileColumn(): void {
    this.fileColumns.push(this.buildFileColumn());
  }

  doRemoveFileColumn(index: number): void {
    this.fileColumns.removeAt(index);
  }

  doAddCurrencyRoute(): void {
    this.currencyRoutes.push(this.buildCurrencyRoute());
  }

  doRemoveCurrencyRoute(index: number): void {
    this.currencyRoutes.removeAt(index);
  }

  get currencyRoutes(): FormArray {
    return this.form.get('currency_based_routing') as FormArray;
  }

  get currencyRoutesList(): PaymentRoute[] {
    return this.value.currency_based_routing ? Object.values(this.value.currency_based_routing) : [];
  }

  currencyRouteAt(index: number): FormGroup {
    return this.currencyRoutes.at(index) as FormGroup;
  }

  currencyRouteControl(index: number, name: keyof PaymentRoute): FormControl {
    return this.currencyRouteAt(index)?.get(name) as FormControl;
  }

  private setCurrencyRoutes(routes: Record<string, PaymentRoute> = {}): void {
    const disabled = this.form.disabled;

    this.currencyRoutes.clear({ emitEvent: false });
    if (routes) {
      Object.values(routes).forEach((route) => {
        this.currencyRoutes.push(this.buildCurrencyRoute(route), { emitEvent: false });
      });
    }
    this.validateCurrencies();

    if (disabled) {
      this.form.disable({ emitEvent: false });
    }
  }

  private toFormValue(value: PaymentsSettings): Record<string, unknown> {
    return { ...value, currency_based_routing: Object.values(value.currency_based_routing ?? {}) };
  }

  private buildCurrencyRoute(route?: PaymentRoute): FormGroup {
    const currency = new FormControl<string>((route?.currency ?? '').toUpperCase(), [
      Validators.required,
      Validators.pattern(/^[A-Z]{3}$/),
      (control: AbstractControl) => this.uniqueCurrency(control)
    ]);
    const swift = new FormControl<string>((route?.swift ?? '').toUpperCase(), [Validators.required, Validators.pattern(/^([A-Z0-9]{8}|[A-Z0-9]{11})$/)]);
    this.uppercaseControl(currency);
    this.uppercaseControl(swift);

    return new FormGroup({
      currency,
      account_no: new FormControl<string>(route?.account_no ?? '', [Validators.required, Validators.maxLength(20)]),
      swift,
      accounting: new FormControl<string>(route?.accounting ?? '', [Validators.maxLength(100)])
    });
  }

  private uppercaseControl(control: FormControl<string | null>): void {
    control.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (value && value !== value.toUpperCase()) {
          control.setValue(value.toUpperCase(), { emitEvent: false });
        }
      });
  }

  // A currency may only be routed once. Only the later occurrences are marked, so the first route stays valid.
  private uniqueCurrency(control: AbstractControl): ValidationErrors | null {
    const route = control.parent;
    const routes = route?.parent as FormArray | null;
    const currency = (control.value ?? '').trim().toUpperCase();
    if (!route || !routes || !currency) {
      return null;
    }

    const index = routes.controls.indexOf(route);
    const duplicate = routes.controls
      .slice(0, index)
      .some((other) => (other.get('currency')?.value ?? '').trim().toUpperCase() === currency);
    return duplicate ? { duplicate: true } : null;
  }

  // The duplicate check depends on the other routes, so all currencies are revalidated whenever a route changes.
  // Skipped while disabled: revalidating during form.disable() would mark the form enabled again.
  private validateCurrencies(): void {
    if (this.currencyRoutes.disabled) {
      return;
    }

    this.currencyRoutes.controls.forEach((route) => {
      route.get('currency')?.updateValueAndValidity({ emitEvent: false });
    });
  }

  // Columns are only used by the generic Excel file, so they are kept out of the settings for any other format.
  private updateFileColumnsStatus(): void {
    if (!this.showFileColumns()) {
      this.fileColumns.disable({ emitEvent: false });
    } else if (this.form.enabled) {
      this.fileColumns.enable({ emitEvent: false });
    }
  }

  private setFileColumns(columns: PaymentFileColumn[] = []): void {
    const disabled = this.form.disabled;

    this.fileColumns.clear({ emitEvent: false });
    columns.forEach((column) => this.fileColumns.push(this.buildFileColumn(column), { emitEvent: false }));

    if (disabled) {
      this.form.disable({ emitEvent: false });
    }
  }

  private buildFileColumn(column?: PaymentFileColumn): FormGroup {
    return new FormGroup({
      key: new FormControl<string>(column?.key ?? '', [Validators.required, Validators.maxLength(100)]),
      width: new FormControl<number>(column?.width ?? 20, [Validators.required, Validators.min(1)]),
      style: new FormControl<PaymentFileColumn['style']>(column?.style ?? 'text', [Validators.required]),
      format: new FormControl<string>(column?.format ?? null, [Validators.maxLength(100)])
    });
  }

  get fileFormat(): FormControl {
    return this.form.get('file-format') as FormControl;
  }

  get frequencyMode(): FormControl {
    return this.form.get('frequency_mode') as FormControl;
  }

  get fileColumns(): FormArray {
    return this.form.get('file-columns') as FormArray;
  }

  fileColumnAt(index: number): FormGroup {
    return this.fileColumns.at(index) as FormGroup;
  }

  fileColumnControl(index: number, name: keyof PaymentFileColumn): FormControl {
    return this.fileColumnAt(index)?.get(name) as FormControl;
  }

  fileColumnStyle(style: PaymentFileColumn['style']): string {
    return { text: 'Tekst', date: 'Dato', amount: 'Beløb' }[style] ?? style;
  }

  weekDays(frequency: number[]): string[] {
    return frequency.map((day) => ['Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag', 'Søndag'][day - 1]);
  }

  showUsePNo(): boolean {
    return ['NordeaCsv', 'NykreditCsv'].includes(this.fileFormat.value);
  }

  showFileColumns(): boolean {
    return this.fileFormat.value === 'PaymentsXlsx';
  }

}
